import React, { useState, useEffect, useRef } from 'react';
import { useToggle } from 'react-use';
import { Modal, Form, Select, Button } from 'antd';
import { Calendar, momentLocalizer } from 'react-big-calendar';
import map from 'lodash/map';
import find from 'lodash/find';
import filter from 'lodash/filter';
import moment from 'moment';
import compact from 'lodash/compact';
import split from 'lodash/split';
import get from 'lodash/get';
import cn from 'classnames';

import CustomEvent from './CustomEvent';
import RangeTimePicker from '@components/controls/RangeTimePicker';
import getCurrentWeekDate from '../../utils/getCurrentWeekDates';
import Badge from '@components/snippets/Badge';
import Color from '../../constants/Color';

const now = new Date();
const { Option } = Select;

const MODAL_WIDTH = 320;
const MODAL_HEIGHT = 287;

const localizer = momentLocalizer(moment);

const validationConfig = (t) => ({
  rules: [{ required: true, message: t('field-required') }],
});

function CalendarContainer({
  t,
  allPlaylists,
  playlists,
  onAddPlaylistToCalendar,
  onChangeIntervals,
  onDeletePlaylist,
  togglePreview,
  onFetchContents,
  isOverModal,
  setOverModal,
}) {
  const [events, setEvents] = useState([]);
  const [showForm, toggleForm] = useToggle(false);
  const [initialValues, setInitialValues] = useState({});
  const [playlistFormSettings, setPlaylistFormSettings] = useState({
    isOpen: false,
  });
  const [style, setStyle] = useState({});
  const containerRef = useRef(null);
  const formRef = useRef(null);
  const modalRef = useRef(null);

  const toggle = (type, slot) => {
    setPlaylistFormSettings({
      isOpen: !playlistFormSettings.isOpen,
      type,
    });

    if (type === 'edit' || (playlistFormSettings.type === 'edit' && !type))
      togglePreview(slot);
  };

  const calcModalPosition = (e) => {
    const windowWidth = window.innerWidth;
    const windowHeight = window.innerHeight;
    let margin = 0;
    let left = e.pageX;
    let top = e.clientY;
    let width;

    if (windowWidth < left + MODAL_WIDTH) {
      left -= MODAL_WIDTH;
    }

    if (windowHeight < top + MODAL_HEIGHT) {
      top -= MODAL_HEIGHT;
    }

    if (windowWidth < 767) {
      left = `calc(50% - ${MODAL_WIDTH / 2})`;
      width = '100%';
      margin = '0 auto';
    }

    return {
      margin,
      left,
      top,
      width,
    };
  };

  const onCancel = () => {
    setEvents(filter(events, ({ id }) => id !== 'NEW'));
    toggle();
    toggleForm();
  };

  const convertToMoment = (st, end, day) => ({
    begin: moment(
      new Date(
        now.getFullYear(),
        now.getMonth(),
        day,
        Math.trunc(st / 60),
        st % 60,
        0
      )
    ),
    end: moment(
      new Date(
        now.getFullYear(),
        now.getMonth(),
        day,
        Math.trunc(end / 60),
        end % 60,
        0
      )
    ),
  });

  const calcInterval = (time) => {
    const [stHours, stMinutes] = split(time.begin.format('HH:mm'), ':');
    const [endHours, endMinutes] = split(time.end.format('HH:mm'), ':');
    const st = +stHours * 60 + +stMinutes;
    const end = +endHours * 60 + +endMinutes;

    return {
      st,
      end,
    };
  };

  const onAddPlaylist = (values) => {
    const { time, playlistId } = values;
    let day = values.day;
    day = day - 1 < 0 ? 6 : day - 1;
    toggle('create');
    const { st, end } = calcInterval(time);

    map(playlists, (playlist) => {
      const interval = get(playlist, ['intervals', day]);

      if (!!interval) {
        changeInterval(interval, playlist, st, end, day);
      }
    });

    onAddPlaylistToCalendar(playlistId)({
      time,
      day: day,
    });
    onCancel();
  };

  const onEditPlaylist = (slot, e) => {
    setStyle(calcModalPosition(e));
    const day = slot.start.getDay();
    onFetchContents(slot.id);
    setInitialValues({
      guid: slot.guid,
      day: day - 1 < 0 ? 6 : day - 1,
      playlistId: slot.id,
      time: {
        begin: moment(slot.start, 'HH:mm'),
        end: moment(slot.end, 'HH:mm'),
      },
    });

    toggleForm();
    toggle('edit', slot);
  };

  const onSubmit = (callback = () => {}) => () => {
    if (formRef && formRef.current) {
      formRef.current.submit();
    }
  };

  const onDelete = () => {
    onDeletePlaylist(initialValues.guid);
    onCancel();
  };

  const onSelectSlot = ({ bounds, start, end }) => {
    const day = start.getDay();

    if (bounds) {
      setStyle({
        margin: 0,
        left:
          window.innerWidth < bounds.left + MODAL_WIDTH
            ? bounds.left - MODAL_WIDTH
            : bounds.left,
        top:
          bounds.top + MODAL_HEIGHT > window.innerHeight
            ? bounds.top - MODAL_HEIGHT
            : bounds.top,
      });
    }

    setInitialValues({
      day: day,
      time: {
        begin: moment(start, 'HH:mm'),
        end: moment(end, 'HH:mm'),
      },
    });

    setEvents([...events, { id: 'NEW', title: t('not-chosen'), start, end }]);
    toggleForm();
    toggle('create');
  };

  const mapEvents = (data = []) => {
    const events = [];
    const currentWeekDays = getCurrentWeekDate();

    map(data, ({ guid, name, intervals, ...rest }) => {
      map(intervals, ({ st, end }, k) => {
        const day = currentWeekDays[k];

        events.push({
          ...rest,
          guid,
          title: name,
          start: new Date(
            day.year(),
            day.month(),
            day.date(),
            Math.trunc(st / 60),
            st % 60,
            0
          ),
          end: new Date(
            day.year(),
            day.month(),
            day.date(),
            Math.trunc(end / 60),
            end % 60,
            0
          ),
        });
      });
    });

    return compact(events);
  };

  const changeInterval = (interval, playlist, st, end, day) => {
    if (interval.st <= st && interval.end >= end) {
      if (interval.end !== end) {
        onAddPlaylistToCalendar(playlist.id)({
          time: convertToMoment(end, interval.end, day),
          day: day,
        });
      }
      onChangeIntervals({
        guid: playlist.guid,
        day: day,
        time: convertToMoment(interval.st, st, day),
      });
    } else if (st <= interval.st && end <= interval.end && end >= interval.st) {
      if (end === interval.end) {
        onDeletePlaylist(playlist.guid);
      } else {
        onChangeIntervals({
          guid: playlist.guid,
          day: day,
          time: convertToMoment(end, interval.end, day),
        });
      }
    } else if (st >= interval.st && end >= interval.end && st <= interval.end) {
      if (interval.st === st) {
        onDeletePlaylist(playlist.guid);
      } else {
        onChangeIntervals({
          guid: playlist.guid,
          day: day,
          time: convertToMoment(interval.st, st, day),
        });
      }
    } else if (st < interval.st && end > interval.end) {
      onDeletePlaylist(playlist.guid);
    }
  };

  const handleEditPlaylist = (values) => {
    const day = values.day;
    const { st, end } = calcInterval(values.time);

    map(playlists, (playlist) => {
      if (playlist.guid === values.guid) return;
      const interval = get(playlist, ['intervals', day]);

      if (!!interval) {
        changeInterval(interval, playlist, st, end, day);
      }
    });

    onChangeIntervals(values);
    onCancel();
  };

  const modalOn = () => setOverModal(true);
  const modalOff = () => setOverModal(false);

  useEffect(() => {
    const modal = document.querySelector('.calendar__modal > .ant-modal');

    if (formRef.current) {
      modal.addEventListener('mouseenter', modalOn);
      modal.addEventListener('mouseleave', modalOff);
    }

    return () => {
      modal && modal.removeEventListener('mouseenter', modalOn);
      modal && modal.removeEventListener('mouseleave', modalOff);
    };
  }, [formRef.current]);

  useEffect(() => {
    setEvents(mapEvents(playlists));
  }, [playlists]);

  return (
    <div ref={containerRef} className="calendar">
      <Modal
        ref={modalRef}
        title={
          playlistFormSettings.type === 'create' ? t('add-playlist') : t('edit')
        }
        visible={playlistFormSettings.isOpen}
        wrapClassName={cn('calendar__modal', {
          'calendar__modal--active': isOverModal,
        })}
        onCancel={onCancel}
        style={style}
        mask={false}
        width={320}
        footer={[
          playlistFormSettings.type === 'edit' && (
            <Button onClick={onDelete} type="danger" ghost>
              {t('delete')}
            </Button>
          ),
          <Button onClick={onSubmit()} type="primary">
            {playlistFormSettings.type === 'create' ? t('add') : t('save')}
          </Button>,
        ]}
      >
        {showForm && (
          <Form
            ref={formRef}
            name="playlist"
            initialValues={initialValues}
            className="calendar__add-playlist-form"
            layout="vertical"
            onFinish={
              playlistFormSettings.type === 'create'
                ? onAddPlaylist
                : handleEditPlaylist
            }
          >
            <Form.Item name="guid" noStyle />
            <Form.Item name="day" noStyle />
            <Form.Item
              className="calendar__add-playlist-item"
              name="playlistId"
              label={t('select-playlist')}
              {...validationConfig(t)}
            >
              <Select
                onSelect={onFetchContents}
                dropdownClassName="calendar__modal-dropdown"
              >
                {map(
                  allPlaylists,
                  ({ id, name, color, ...playlist }, index) => (
                    <Option value={id} key={index}>
                      <Badge color={color} /> <div>{name}</div>
                    </Option>
                  )
                )}
              </Select>
            </Form.Item>
            <Form.Item
              className="calendar__add-playlist-item"
              name="time"
              label={t('play-playlist')}
              {...validationConfig(t)}
            >
              <RangeTimePicker />
            </Form.Item>
          </Form>
        )}
      </Modal>
      <Calendar
        localizer={localizer}
        toolbar={false}
        allDay={false}
        selectable
        defaultView="week"
        startAccessor="start"
        endAccessor="end"
        culture={window.localStorage.getItem('i18nextLng')}
        defaultDate={now}
        events={events}
        // style={{ height: !height ? 0 : height }}
        components={{ event: CustomEvent }}
        onSelectEvent={onEditPlaylist}
        onSelectSlot={onSelectSlot}
        step={15}
        eventPropGetter={function({ id }, start, end) {
          const timeDiff = Math.floor(((end - start) % 86400000) / 3600000);

          const playlist =
            find(allPlaylists, ({ id: playlistId }) => playlistId === id) || {};
          const { color = 'grey' } = playlist;

          return {
            className: cn('calendar__event', {
              'calendar__event--short': timeDiff < 1,
              'calendar__event--new': id === 'NEW',
            }),
            style: {
              background: Color[color],
              border: 'none',
            },
          };
        }}
        formats={{
          dayFormat: (date, culture, localizer) => localizer.format(date, 'dd'),
        }}
        timeslots={8}
      />
    </div>
  );
}

export default CalendarContainer;
